import { supabase } from "../lib/supabase";
import type { Booking } from "../types/booking";
import { InvoiceLine } from "../types/invoice";
import { calculatePricingFromTemplates } from "./unifiedPricingService";
import { getEffectiveCommodityInspection } from "../utils/commodityInspectionUtils";
import { getPostExportLicenseFee } from "../utils/postExportLicenseUtils";
import {
  sortInvoiceLinesByCostOverviewOrder,
} from "./costOverviewDisplayOrderService";
import { resolveBookingPalletCount } from "../utils/bookingTransportUnits";
import { getPalletThreshold } from "./lmdRatesService";


// Type cast supabase to avoid TypeScript errors
const typedSupabase = supabase as unknown as SupabaseClient;



interface PricingComponent {
  id: number;
  name: string;
  description: string | null;
  component_type: string;
  calculation_type: string;
}

interface PricingRule {
  id: number;
  name: string;
  valid_from: string;
  valid_to: string | null;
  transport_type: number | null;
  origin_port: number | null;
  destination_port: number | null;
  shipment_type: number | null;
  container_size: number | null;
  incoterm: number | null;
}


/**
 * Find matching pricing rules for a booking
 * @param booking The booking to find matching rules for
 * @param matchDate Optional date to use for matching rules (e.g., container ETD date)
 */
export const findMatchingPricingRules = async (booking: Booking, matchDate?: string | null) => {
  try {
    // Use provided matchDate if available, otherwise use today
    const dateToMatch = matchDate || new Date().toISOString().split('T')[0];

    console.log(`Finding pricing rules for date: ${dateToMatch}`);

    // Get container origin and destination ports if available
    let originPort = booking.origin_port;
    let destinationPort = booking.destination_port;

    // Check for container ports to override booking ports
    if (booking.container_info) {
      const containerInfo = Array.isArray(booking.container_info)
        ? booking.container_info[0]
        : booking.container_info;
      
      // If container has origin port, use that instead of booking origin port
      if (containerInfo.origin_port) {
        const containerOriginPort = Array.isArray(containerInfo.origin_port)
          ? containerInfo.origin_port[0]
          : containerInfo.origin_port;
        
        if (containerOriginPort && containerOriginPort.id) {
          console.log(`Using container origin port ${containerOriginPort.id} instead of booking origin port ${booking.origin_port}`);
          originPort = containerOriginPort.id;
        }
      }
      
      // If container has destination port, use that instead of booking destination port
      if (containerInfo.destination_port) {
        const containerDestPort = Array.isArray(containerInfo.destination_port)
          ? containerInfo.destination_port[0]
          : containerInfo.destination_port;
        
        if (containerDestPort && containerDestPort.id) {
          console.log(`Using container destination port ${containerDestPort.id} instead of booking destination port ${booking.destination_port}`);
          destinationPort = containerDestPort.id;
        }
      }
    }

    // Query for matching rules
    const { data, error } = await typedSupabase
      .from("pricing_rules")
      .select("*")
      .lte("valid_from", dateToMatch)
      .or(`valid_to.gte.${dateToMatch},valid_to.is.null`)
      .eq("is_active", true);

    if (error) throw error;

    if (!data || data.length === 0) {
      return { rules: [], error: null, allRules: [], rejectedRules: [] };
    }

    // Helper function to calculate rule specificity (higher = more specific)
    const calculateSpecificity = (rule: PricingRule): number => {
      let specificity = 0;
      if (rule.origin_port !== null) specificity++;
      if (rule.destination_port !== null) specificity++;
      if (rule.transport_type !== null) specificity++;
      if (rule.shipment_type !== null) specificity++;
      if (rule.container_size !== null) specificity++;
      if (rule.incoterm !== null) specificity++;
      return specificity;
    };

    // Helper function to get rejection reasons
    const getRejectionReasons = (rule: PricingRule): string[] => {
      const reasons: string[] = [];
      
      if (rule.transport_type !== null && rule.transport_type !== booking.transport_type) {
        reasons.push("Transport type mismatch");
      }
      if (rule.origin_port !== null && rule.origin_port !== originPort) {
        reasons.push("Origin port mismatch");
      }
      if (rule.destination_port !== null && rule.destination_port !== destinationPort) {
        reasons.push("Destination port mismatch");
      }
      if (rule.shipment_type !== null && rule.shipment_type !== booking.booking_type) {
        reasons.push("Shipment type mismatch");
      }
      if (rule.incoterm !== null && rule.incoterm !== booking.booking_incoterm) {
        reasons.push("Incoterm mismatch");
      }
      
      return reasons;
    };

    // Separate matching and rejected rules
    const matchingRules: PricingRule[] = [];
    const rejectedRules: Array<{ rule: PricingRule; reasonsRejected: string[] }> = [];

    data.forEach((rule: PricingRule) => {
      const rejectionReasons = getRejectionReasons(rule);
      
      if (rejectionReasons.length === 0) {
        matchingRules.push(rule);
      } else {
        rejectedRules.push({ rule, reasonsRejected: rejectionReasons });
      }
    });

    // Sort matching rules by specificity (most specific first)
    const sortedRules = matchingRules.sort((a, b) => {
      const specificityA = calculateSpecificity(a);
      const specificityB = calculateSpecificity(b);
      return specificityB - specificityA; // Higher specificity first
    });

    return { 
      rules: sortedRules, 
      error: null, 
      allRules: data, 
      rejectedRules 
    };
  } catch (error) {
    console.error("Error finding matching pricing rules:", error);
    return { rules: [], error, allRules: [], rejectedRules: [] };
  }
};

/** Transport type IDs: 1 = air, 2 = sea (seafreight) */
const TRANSPORT_TYPE_AIR = 1;
const TRANSPORT_TYPE_SEA = 2;

export type FclTariffOption = {
  containerSizeId: number | null;
  containerSizeName: string;
  amount: number;
};

export type EstimatedTransportResult = {
  amount: number;
  currency: "EUR";
  source: "tariff" | "none";
  /** When source is tariff: rate from tariff (per kg, per CBM, or per container). */
  tariffRate?: number;
  /** When source is tariff: "per_kg" | "per_cbm" | "per_container". */
  tariffUnit?: "per_kg" | "per_cbm" | "per_container";
  /** Total CBM from products (used for sea LCL calculation). */
  totalVolume?: number;
  /** Chargeable weight (air) or N/A for sea. */
  chargeableWeight?: number;
  /** FCL without known container size: one option per matching container size. */
  fclTariffOptions?: FclTariffOption[];
};

type TariffLookupRow = {
  id: number;
  price: number;
  transport_type: number;
  container_size: number | null;
};

async function buildFclTariffOptions(tariffs: TariffLookupRow[]): Promise<FclTariffOption[]> {
  const sizeIds = [
    ...new Set(
      tariffs
        .map((t) => t.container_size)
        .filter((id): id is number => typeof id === "number"),
    ),
  ];

  const sizeNameById: Record<number, string> = {};
  if (sizeIds.length > 0) {
    const { data: sizeRows } = await supabase
      .from("container_size")
      .select("id, name")
      .in("id", sizeIds);
    if (sizeRows) {
      for (const row of sizeRows) {
        sizeNameById[row.id] = row.name;
      }
    }
  }

  const seen = new Set<number | "unknown">();
  const options: FclTariffOption[] = [];
  for (const tariff of tariffs) {
    if (typeof tariff.price !== "number") continue;
    const sizeId = tariff.container_size ?? null;
    const dedupeKey = sizeId ?? "unknown";
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    options.push({
      containerSizeId: sizeId,
      containerSizeName:
        sizeId != null ? (sizeNameById[sizeId] ?? `Size ${sizeId}`) : "Unknown size",
      amount: Math.round(tariff.price * 100) / 100,
    });
  }

  return options;
}

/**
 * Get estimated transport cost (freight only: airfare / LCL / FCL) from tariffs.
 * Does not include insurance, LMD, or other components.
 * For sea: uses CBM only (LCL = CBM × rate; FCL = price per container).
 */
function extractPortId(port: unknown): number | null {
  if (typeof port === "number") return port;
  if (port && typeof port === "object" && "id" in port && typeof (port as { id: unknown }).id === "number") {
    return (port as { id: number }).id;
  }
  if (Array.isArray(port) && port.length > 0) return extractPortId(port[0]);
  return null;
}

function resolveOriginDestinationPorts(booking: Booking): { originPort: number; destinationPort: number } | null {
  // Prefer container ports when available (may be IDs or joined objects)
  if (booking.container_info) {
    const containers = Array.isArray(booking.container_info) ? booking.container_info : [booking.container_info];
    const container = containers[0];
    if (container) {
      const o = extractPortId(container.origin_port);
      const d = extractPortId(container.destination_port);
      if (o && d) return { originPort: o, destinationPort: d };
    }
  }
  // Fallback to booking ports
  const o = extractPortId(booking.origin_port);
  const d = extractPortId(booking.destination_port);
  if (o && d) return { originPort: o, destinationPort: d };
  return null;
}

/** FCL container_size FK on containers / tariffs (not populated on most bookings today). */
async function resolveFclContainerSizeId(booking: Booking): Promise<number | undefined> {
  if (booking.booking_type !== 2) return undefined;

  if (booking.container_info) {
    const containerInfo = Array.isArray(booking.container_info)
      ? booking.container_info[0]
      : booking.container_info;
    const fromJoin = (containerInfo as { container_size?: number | null })?.container_size;
    if (typeof fromJoin === "number") return fromJoin;
  }

  if (!booking.container_id) return undefined;

  const { data: container } = await supabase
    .from("containers")
    .select("container_size")
    .eq("id", booking.container_id)
    .single();

  if (container?.container_size != null) return container.container_size;
  return undefined;
}

export const getEstimatedTransportCostForBooking = async (
  booking: Booking
): Promise<EstimatedTransportResult> => {
  try {
    const ports = resolveOriginDestinationPorts(booking);
    if (!booking.transport_type || !ports) {
      console.warn("getEstimatedTransportCostForBooking: Missing required fields", {
        transport_type: booking.transport_type,
        origin_port: ports?.originPort ?? booking.origin_port,
        destination_port: ports?.destinationPort ?? booking.destination_port,
      });
      return { amount: 0, currency: "EUR", source: "none" } as EstimatedTransportResult;
    }
    const { originPort, destinationPort } = ports;

    const tariffDate = resolvePricingDate(booking);

    const isSeafreight = booking.transport_type === TRANSPORT_TYPE_SEA;
    const isFCL = booking.booking_type === 2;
    const containerSize = isFCL ? await resolveFclContainerSizeId(booking) : undefined;

    const fclSizeUnknown = isFCL && containerSize == null;

    let q = supabase
      .from("tariffs")
      .select("id, price, transport_type, container_size")
      .eq("transport_type", booking.transport_type)
      .eq("origin_port", originPort)
      .eq("destination_port", destinationPort)
      .lte("from", tariffDate)
      .or(`to.is.null,to.gte.${tariffDate}`)
      .order("from", { ascending: false });

    // For sea freight, filter by booking type (LCL vs FCL)
    // booking_type: 1 = LCL, 2 = FCL
    if (isSeafreight && booking.booking_type) {
      q = q.eq("bookings_type", booking.booking_type);
    }

    if (containerSize != null) {
      q = q.eq("container_size", containerSize);
    } else if (!isFCL) {
      // Air and LCL tariffs use null container_size
      q = q.is("container_size", null);
    }
    // FCL without known container size: match any container_size for route/date/type

    const { data: tariffs, error: tariffError } = await q.limit(fclSizeUnknown ? 20 : 1);
    
    if (tariffError) {
      console.error("getEstimatedTransportCostForBooking: Error fetching tariffs", tariffError);
      return { amount: 0, currency: "EUR", source: "none" } as EstimatedTransportResult;
    }

    const tariffRows = (tariffs ?? []) as TariffLookupRow[];

    // Unwrap product_info: Product[] or Product[][] (same as Lean Customs modal)
    const rawProducts = booking.product_info;
    const products: unknown[] = Array.isArray(rawProducts)
      ? Array.isArray(rawProducts[0])
        ? (rawProducts as unknown[][]).flat()
        : rawProducts
      : rawProducts
        ? [rawProducts]
        : [];
    const totalWeight = products.reduce<number>(
      (s, p) => s + (Number((p as { gross_weight?: unknown })?.gross_weight) || 0),
      0
    );
    const totalVolume = products.reduce<number>(
      (s, p) => s + (Number((p as { cbm?: unknown })?.cbm) || 0),
      0
    );

    if (fclSizeUnknown && tariffRows.length > 0) {
      const fclTariffOptions = await buildFclTariffOptions(tariffRows);
      if (fclTariffOptions.length > 1) {
        return {
          amount: 0,
          currency: "EUR",
          source: "none",
          tariffUnit: "per_container",
          totalVolume: totalVolume > 0 ? Math.round(totalVolume * 100) / 100 : undefined,
          fclTariffOptions,
        };
      }
      if (fclTariffOptions.length === 1) {
        const only = fclTariffOptions[0];
        return {
          amount: only.amount,
          currency: "EUR",
          source: "tariff",
          tariffRate: only.amount,
          tariffUnit: "per_container",
          totalVolume: totalVolume > 0 ? Math.round(totalVolume * 100) / 100 : undefined,
        };
      }
    }

    const selectedTariff = tariffRows.length > 0 ? tariffRows[0] : null;

    if (!selectedTariff) {
      console.warn("getEstimatedTransportCostForBooking: No tariff found", {
        transport_type: booking.transport_type,
        origin_port: originPort,
        destination_port: destinationPort,
        booking_type: booking.booking_type,
        tariffDate,
        containerSize,
      });
    }

    if (!selectedTariff || typeof selectedTariff.price !== "number") {
      return { amount: 0, currency: "EUR", source: "none" } as EstimatedTransportResult;
    }

    const isAir = booking.transport_type === TRANSPORT_TYPE_AIR;

    let amount = 0;
    let tariffRate: number | undefined;
    let tariffUnit: "per_kg" | "per_cbm" | "per_container" | undefined;
    let chargeableWeight: number | undefined;

    if (isAir) {
      const volumeWeight = totalVolume * 167;
      const cw = Math.max(totalWeight, volumeWeight);
      chargeableWeight = cw;
      tariffRate = selectedTariff.price;
      tariffUnit = "per_kg";
      amount = cw * selectedTariff.price;
    } else if (isSeafreight) {
      if (isFCL) {
        amount = selectedTariff.price;
        tariffRate = selectedTariff.price;
        tariffUnit = "per_container";
      } else {
        // Sea LCL: use CBM only for calculation
        amount = totalVolume * selectedTariff.price;
        tariffRate = selectedTariff.price;
        tariffUnit = "per_cbm";
      }
    }

    const result: EstimatedTransportResult = {
      amount: Math.round(amount * 100) / 100,
      currency: "EUR",
      source: "tariff",
      totalVolume: totalVolume > 0 ? Math.round(totalVolume * 100) / 100 : undefined,
      chargeableWeight,
    };
    if (tariffRate != null) result.tariffRate = tariffRate;
    if (tariffUnit) result.tariffUnit = tariffUnit;
    return result;
  } catch (e) {
    console.error("getEstimatedTransportCostForBooking:", e);
    return { amount: 0, currency: "EUR", source: "none" } as EstimatedTransportResult;
  }
};


/**
 * Resolve the pricing/tariff date with priority: container ETD (departure) >
 * confirmed_pick_up_date > today. Used for LeanCustoms freight estimates and
 * invoice template matching.
 */
function resolvePricingDate(booking: Booking): string {
  if (booking.container_info) {
    const container = Array.isArray(booking.container_info)
      ? booking.container_info[0]
      : booking.container_info;
    if (container?.etd) return container.etd;
  }
  if (booking.confirmed_pick_up_date) {
    const d = new Date(booking.confirmed_pick_up_date);
    if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
  }
  return new Date().toISOString().split("T")[0];
}

/**
 * Derive delivery-related options from booking data for the unified pricing service.
 */
function resolveDeliveryOptions(booking: Booking): {
  deliveryType: "delivery" | "storage";
  lmdCountryCode: string | null;
  destinationCountryCode: string | null;
} {
  let hasDelivery = false;
  let lmdCountryCode: string | null = null;

  if (booking.booking_delivery_info) {
    const deliveries = Array.isArray(booking.booking_delivery_info)
      ? booking.booking_delivery_info
      : [booking.booking_delivery_info];
    if (deliveries.length > 0) {
      hasDelivery = true;
      lmdCountryCode = deliveries[0].country ?? null;
    }
  }

  let destinationCountryCode: string | null = null;
  if (booking.destination_port_info) {
    const port = Array.isArray(booking.destination_port_info)
      ? booking.destination_port_info[0]
      : booking.destination_port_info;
    destinationCountryCode = port?.country ?? null;
  }

  return {
    deliveryType: hasDelivery ? "delivery" : "storage",
    lmdCountryCode,
    destinationCountryCode,
  };
}

/** Import/freight forwarding invoice lines are never VAT-rated. */
function resolveVatPercentage(_componentName?: string, _componentType?: string): number {
  return 0;
}

/**
 * Generate invoice lines using the unified pricing service.
 * Delegates all cost calculation to calculatePricingFromTemplates so invoice
 * costs exactly match the calculator output.
 */
export const generateInvoiceLinesFromPricing = async (
  booking: Booking
): Promise<{
  lines: InvoiceLine[];
  currency: string;
  error: unknown;
  templateSelectionInfo?: {
    selectedTemplate: any | null;
    totalTemplatesFound: number;
    matchingCriteria: string[];
  };
  ruleBreakdown?: any;
}> => {
  try {
    const pricingDate = resolvePricingDate(booking);
    const ports = resolveOriginDestinationPorts(booking);
    const containerSize = await resolveFclContainerSizeId(booking);
    const { deliveryType, lmdCountryCode, destinationCountryCode } =
      resolveDeliveryOptions(booking);

    const originPortId = ports?.originPort ?? booking.origin_port;
    const destinationPortId = ports?.destinationPort ?? booking.destination_port;

    // Fetch tariff using resolved ports/date, filtered by LCL vs FCL
    // (same rules as getEstimatedTransportCostForBooking — without this, sea
    // invoices can pick an FCL flat rate and multiply it by LCL volume).
    const isSeafreight = booking.transport_type === TRANSPORT_TYPE_SEA;
    const isFCL = booking.booking_type === 2;
    const fclContainerSize = isFCL ? containerSize : undefined;

    let tariffQuery = supabase
      .from("tariffs")
      .select("*")
      .eq("transport_type", booking.transport_type)
      .eq("origin_port", originPortId)
      .eq("destination_port", destinationPortId)
      .lte("from", pricingDate)
      .or(`to.is.null,to.gte.${pricingDate}`)
      .order("from", { ascending: false });

    if (isSeafreight && booking.booking_type) {
      tariffQuery = tariffQuery.eq("bookings_type", booking.booking_type);
    }
    if (fclContainerSize != null) {
      tariffQuery = tariffQuery.eq("container_size", fclContainerSize);
    } else if (!isFCL) {
      tariffQuery = tariffQuery.is("container_size", null);
    }

    const { data: tariffs } = await tariffQuery.limit(1);
    const selectedTariff = tariffs && tariffs.length > 0 ? tariffs[0] : null;

    // Calculate totals from booking products
    const products = Array.isArray(booking.product_info) ? booking.product_info.flat() : [];
    const totalCartons = products.reduce((sum, p) => sum + (p.cartons || 0), 0);
    const deliveryInfo = Array.isArray(booking.booking_delivery_info)
      ? booking.booking_delivery_info[0]
      : booking.booking_delivery_info;
    const storedPallets =
      (deliveryInfo?.delivery_pallets && deliveryInfo.delivery_pallets > 0
        ? deliveryInfo.delivery_pallets
        : null) ??
      (booking.total_actual_pallets && booking.total_actual_pallets > 0
        ? booking.total_actual_pallets
        : null);
    const totalWeight = products.reduce((sum, p) => sum + (p.gross_weight || 0), 0);
    const totalVolume = products.reduce((sum, p) => sum + (p.cbm || 0), 0);
    const [totalPallets, palletThreshold] = await Promise.all([
      resolveBookingPalletCount({
        totalCartons,
        totalVolume,
        storedPallets,
      }),
      getPalletThreshold(),
    ]);
    const totalValue = products.reduce((sum, p) => sum + (p.value || 0), 0);
    const uniqueHsCodesCount = new Set(products.map(p => p.hs_code).filter(Boolean)).size;

    const pricingContext = {
      transportTypeId: booking.transport_type,
      bookingTypeId: booking.booking_type,
      incotermId: booking.booking_incoterm,
      originPortId,
      destinationPortId,
      containerSize,
      matchDate: pricingDate,
    };

    const bookingData = {
      totalCartons,
      totalPallets,
      uniqueProducts: products.length,
      totalWeight,
      totalVolume,
      uniqueHsCodesCount,
      totalValue: totalValue > 0 ? totalValue : undefined,
      containerSize,
    };

    const result = await calculatePricingFromTemplates(
      pricingContext,
      bookingData,
      {
        selectedTariff,
        pickupFee: booking.pick_up_fee || 0,
        includeExportClearance: booking.export_clearance === true,
        includeCommodityInspection: getEffectiveCommodityInspection(booking),
        postExportLicenseFee: getPostExportLicenseFee(booking) ?? 0,
        deliveryType,
        lmdCountryCode,
        destinationCountryCode,
        isStorageDelivery: deliveryType === "storage",
        formatEuro: (value: number) => `€${value.toFixed(2)}`,
        // Invoice generation has no i18n context — always use English fallbacks.
        t: (key: string, defaultValue?: string) =>
          defaultValue ||
          ({
            "tools.calculator.cartons": "cartons",
            "tools.calculator.pallets": "pallets",
            "tools.calculator.included": "included",
            "tools.calculator.container": "container",
          } as Record<string, string>)[key] ||
          key,
        filterZeroComponents: true,
        palletThreshold,
      }
    );

    if (result.error) {
      console.error("Unified pricing calculation error:", result.error);
      return {
        lines: [],
        currency: "EUR",
        error: result.error,
        templateSelectionInfo: { selectedTemplate: null, totalTemplatesFound: 0, matchingCriteria: [] },
      };
    }

    // Convert pricing components to invoice lines.
    // Tiered types (per_unique_product / per_product) are a flat fee + extras already
    // baked into totalPrice — never multiply base × HS/product count on the invoice.
    const TIERED_FLAT_TYPES = new Set(["per_unique_product", "per_product"]);
    const lines: InvoiceLine[] = result.components
      .filter((c) => c.totalPrice > 0)
      .map((component) => {
        const isTiered = TIERED_FLAT_TYPES.has(component.calculationType || "");
        const quantity = isTiered ? 1 : (component.quantity || 1);
        // Prefer totalPrice when qty×unit would miss post-calc extras (e.g. HS add-on)
        const qtyTimesUnit = quantity * (component.unitPrice ?? 0);
        const unitOutOfSync = Math.abs(qtyTimesUnit - component.totalPrice) > 0.005;
        const unit_price =
          isTiered || unitOutOfSync
            ? Number((component.totalPrice / Math.max(quantity, 1)).toFixed(4))
            : (component.unitPrice ?? component.totalPrice);

        const description = component.calculationDetails
          ? `${component.name} (${component.calculationDetails})`
          : component.name;

        return {
          id: 0,
          description,
          quantity,
          unit_price,
          vat_percentage: resolveVatPercentage(component.name, component.componentType),
          // Prefer component id so sort matches calculator pricing_component_{id} keys.
          // Keep system types (export_clearance, transport, lmd, …) as their type string.
          cost_type:
            component.componentId && component.componentId > 0
              ? String(component.componentId)
              : component.componentType || "pricing_component",
          calculation_type: component.calculationType || undefined,
          template_source: (result.matchedTemplates?.[0]?.name as string) || undefined,
        };
      });

    // Sort lines by cost_overview_display_order so Moneybird receives them in the right order
    const sortedLines = await sortInvoiceLinesByCostOverviewOrder(lines);

    return {
      lines: sortedLines,
      currency: "EUR",
      error: null,
      templateSelectionInfo: {
        selectedTemplate: result.matchedTemplates?.[0] || null,
        totalTemplatesFound: result.matchedTemplates?.length || 0,
        matchingCriteria: result.matchingCriteria || [],
      },
    };
  } catch (error) {
    console.error("Error generating invoice lines using unified pricing service:", error);
    return {
      lines: [],
      currency: "EUR",
      error,
      templateSelectionInfo: { selectedTemplate: null, totalTemplatesFound: 0, matchingCriteria: [] },
    };
  }
};

const OUTBOUND_ADMIN_LINE_PATTERN =
  /outbound|administration fee outgoing/i;

function isPalletOnlyOutboundDescription(description: string | undefined): boolean {
  return (
    /outbound cost per pallet/i.test(description || "") ||
    /administration fee outgoing pallet/i.test(description || "")
  );
}

function isCartonOnlyAdminDescription(description: string | undefined): boolean {
  return /administration fee outgoing carton/i.test(description || "");
}

/**
 * Replace outbound/administration lines when shipment type (carton vs pallet)
 * no longer matches what autogenerate produced — e.g. 2 cartons billed as pallet.
 */
export const repairOutboundShipmentTypeLines = async (
  booking: Booking,
  lines: InvoiceLine[],
  expectedShipmentType: "carton" | "pallet",
): Promise<{ lines: InvoiceLine[]; changed: boolean }> => {
  if (!lines?.length) return { lines, changed: false };

  const hasWrongType = lines.some((line) => {
    if (!OUTBOUND_ADMIN_LINE_PATTERN.test(line.description || "")) return false;
    if (expectedShipmentType === "carton") {
      return isPalletOnlyOutboundDescription(line.description);
    }
    return isCartonOnlyAdminDescription(line.description);
  });

  if (!hasWrongType) return { lines, changed: false };

  const { lines: generated, error } = await generateInvoiceLinesFromPricing(booking);
  if (error || !generated?.length) return { lines, changed: false };

  const newOutbound = generated.filter((l) =>
    OUTBOUND_ADMIN_LINE_PATTERN.test(l.description || "")
  );
  if (newOutbound.length === 0) return { lines, changed: false };

  const kept = lines.filter(
    (l) => !OUTBOUND_ADMIN_LINE_PATTERN.test(l.description || "")
  );
  return {
    lines: [...kept, ...newOutbound.map((l) => ({ ...l, vat_percentage: 0 }))],
    changed: true,
  };
};

/**
 * Fix saved invoice lines that incorrectly billed Inklaringskosten as
 * quantity = unique HS codes × base fee (e.g. 2 × €45) instead of the
 * tiered flat total (e.g. €45 + 1 × €6.50 = €51.50).
 */
export const repairInklaringskostenInvoiceLines = async (
  booking: Booking,
  lines: InvoiceLine[],
): Promise<{ lines: InvoiceLine[]; changed: boolean }> => {
  if (!lines?.length) return { lines, changed: false };

  const isInklaringLine = (l: InvoiceLine) =>
    /inklaringskosten/i.test(l.description || "") ||
    l.calculation_type === "per_unique_product" ||
    l.calculation_type === "per_product";

  const buggyIndexes = lines
    .map((l, i) => ({ l, i }))
    .filter(({ l }) => isInklaringLine(l) && (l.quantity || 0) > 1);

  if (buggyIndexes.length === 0) return { lines, changed: false };

  const { lines: generated, error } = await generateInvoiceLinesFromPricing(booking);
  if (error || !generated?.length) return { lines, changed: false };

  const corrected = generated.filter(isInklaringLine);
  if (corrected.length === 0) return { lines, changed: false };

  const updated = [...lines];
  let changed = false;

  for (const { l, i } of buggyIndexes) {
    const match =
      corrected.find((g) => {
        const gd = (g.description || "").toLowerCase();
        const ld = (l.description || "").toLowerCase();
        return gd.includes("inklaring") && ld.includes("inklaring");
      }) ||
      corrected.find((g) => g.calculation_type && g.calculation_type === l.calculation_type) ||
      corrected[0];

    if (!match) continue;
    if (
      l.quantity === match.quantity &&
      Math.abs((l.unit_price || 0) - (match.unit_price || 0)) < 0.01
    ) {
      continue;
    }

    updated[i] = {
      ...l,
      quantity: match.quantity,
      unit_price: match.unit_price,
      description: match.description,
      calculation_type: match.calculation_type || l.calculation_type,
    };
    changed = true;
  }

  return { lines: updated, changed };
};
